/**
 * UX13/UX16/UX17: PlacementPanel — contextual panel for product/zone/pin placement.
 * Appears top-left whenever a product, zone type, or pin type is selected.
 * Layer-aware: shows single/continuous toggle for plants/architecture/POIs,
 * and drawing tool buttons for ground surfaces and metadata zones.
 */

import './PlacementPanel.css'
import {
  getPlacementMode,
  setPlacementMode,
  getSelectedProduct,
  getSelectedProductLayer,
  getSelectedZoneType,
  getSelectedPinType,
  setSelectedZoneType,
  setSelectedPinType,
  stopContinuousPlacement,
  onPlacementStateChange,
  getViewer,
  type PlacementMode
} from './state'
import { LAYER_CONFIG } from './LeftPanel'
import { getZoneTypeDef, getPinTypeDef } from '@/constants/zoneTypes'
import { t } from '@/i18n'

/** Layer display configuration */
const LAYER_DISPLAY: Record<string, { icon: string; nameKey: string; nameFallback: string }> = {
  plants: { icon: 'fa-seedling', nameKey: 'layer.plants', nameFallback: 'Plants' },
  architecture: { icon: 'fa-house', nameKey: 'layer.architecture', nameFallback: 'Architecture' },
  ground: { icon: 'fa-layer-group', nameKey: 'layer.ground', nameFallback: 'Ground' },
  metadata: { icon: 'fa-tags', nameKey: 'layer.metadata', nameFallback: 'Metadata' },
}

/** Determines the current panel mode based on state */
type PanelMode =
  | { type: 'product'; layer: string }
  | { type: 'zone' }
  | { type: 'pin' }
  | null

function getPanelMode(): PanelMode {
  if (getSelectedProduct()) {
    return { type: 'product', layer: getSelectedProductLayer() || 'plants' }
  }
  if (getSelectedZoneType()) return { type: 'zone' }
  if (getSelectedPinType()) return { type: 'pin' }
  return null
}

export class PlacementPanel {
  private el: HTMLElement
  private unsubscribe: (() => void) | null = null
  private unsubToolChange: (() => void) | null = null

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div')
    this.el.className = 'placement-panel'
    parent.appendChild(this.el)

    // Subscribe to placement state changes
    this.unsubscribe = onPlacementStateChange(() => this.update())
    this.update()
  }

  /** Call when left panel expand/collapse state changes */
  setLeftPanelExpanded(expanded: boolean): void {
    this.el.classList.toggle('panel-expanded', expanded)
  }

  private update(): void {
    const mode = getPanelMode()

    if (mode) {
      this.render()
      this.subscribeToolChanges()
      // Show after a microtask so the fade-in transition triggers
      requestAnimationFrame(() => this.el.classList.add('visible'))
    } else {
      this.el.classList.remove('visible')
      this.unsubToolChange?.()
      this.unsubToolChange = null
    }
  }

  /** Subscribe to tool changes for layers that show drawing tools */
  private subscribeToolChanges(): void {
    const mode = getPanelMode()
    const needsToolSub = (mode?.type === 'product' && mode.layer === 'ground') || mode?.type === 'zone'
    if (!needsToolSub) {
      this.unsubToolChange?.()
      this.unsubToolChange = null
      return
    }

    // Already subscribed
    if (this.unsubToolChange) return

    const toolManager = getViewer()?.getToolManager()
    if (toolManager) {
      this.unsubToolChange = toolManager.onToolChange(() => this.updateToolButtons())
    }
  }

  private render(): void {
    const mode = getPanelMode()
    if (!mode) return

    const { accentColor, iconHtml, itemName, layerName, bodyHtml, showTools } = this.buildRenderData(mode)

    this.el.style.setProperty('--pp-active-color', accentColor)

    this.el.innerHTML = `
      <div class="pp-header">
        <div class="pp-icon">${iconHtml}</div>
        <div class="pp-header-info">
          <div class="pp-product-name" title="${this.escapeHtml(itemName)}">${this.escapeHtml(itemName)}</div>
          <div class="pp-layer-hint">
            <span class="pp-layer-dot" style="background: ${accentColor};"></span>
            ${layerName}
          </div>
        </div>
        <button class="pp-close" title="${t('placement.stop', 'Stop placement')}">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
      ${bodyHtml}
    `

    // Wire close button
    this.el.querySelector('.pp-close')?.addEventListener('click', () => {
      this.close()
    })

    if (showTools) {
      this.wireToolButtons()
    } else {
      this.wireModeButtons()
    }
  }

  private buildRenderData(mode: NonNullable<PanelMode>) {
    const metadataConfig = LAYER_CONFIG['metadata' as keyof typeof LAYER_CONFIG]
    const metadataColor = metadataConfig?.color || '#8894d1'
    const metadataDisplay = LAYER_DISPLAY.metadata

    if (mode.type === 'zone') {
      const zoneType = getSelectedZoneType()!
      const def = getZoneTypeDef(zoneType)
      const colorHex = '#' + def.color.toString(16).padStart(6, '0')
      return {
        accentColor: metadataColor,
        iconHtml: `<div class="pp-zone-swatch" style="background: ${colorHex};"></div>`,
        itemName: t(def.labelKey, def.label),
        layerName: t(metadataDisplay.nameKey, metadataDisplay.nameFallback),
        bodyHtml: this.renderDrawToolsBody('metadata'),
        showTools: true,
      }
    }

    if (mode.type === 'pin') {
      const pinType = getSelectedPinType()!
      const def = getPinTypeDef(pinType)
      const colorHex = '#' + def.color.toString(16).padStart(6, '0')
      return {
        accentColor: metadataColor,
        iconHtml: `<i class="${def.icon}" style="color: ${colorHex};"></i>`,
        itemName: t(def.labelKey, def.label),
        layerName: t(metadataDisplay.nameKey, metadataDisplay.nameFallback),
        bodyHtml: this.renderPlacementBody(),
        showTools: false,
      }
    }

    // Product mode
    const product = getSelectedProduct()!
    const layer = mode.layer
    const layerConfig = LAYER_CONFIG[layer as keyof typeof LAYER_CONFIG]
    const accentColor = layerConfig?.color || '#92cc09'
    const display = LAYER_DISPLAY[layer] || LAYER_DISPLAY.plants
    return {
      accentColor,
      iconHtml: product.iconUrl
        ? `<img src="${this.escapeHtml(product.iconUrl)}" alt="" />`
        : `<i class="fa-solid ${display.icon}" style="color: ${accentColor};"></i>`,
      itemName: product.name,
      layerName: t(display.nameKey, display.nameFallback),
      bodyHtml: layer === 'ground' ? this.renderDrawToolsBody('ground') : this.renderPlacementBody(),
      showTools: layer === 'ground',
    }
  }

  private renderPlacementBody(): string {
    const mode = getPlacementMode()
    const panelMode = getPanelMode()
    const isProduct = panelMode?.type === 'product'

    let hintText: string
    if (mode === 'linear') {
      const phase = getViewer()?.getToolManager()?.getActiveToolName() === 'linear-placement' ? 'active' : 'idle'
      hintText = phase === 'active'
        ? t('placement.hintLinear', 'Click start point \u00b7 Esc to cancel')
        : t('placement.hintLinear', 'Click start point \u00b7 Esc to cancel')
    } else if (mode === 'continuous') {
      hintText = t('placement.hintContinuous', 'Click to keep placing \u00b7 Esc to stop')
    } else {
      hintText = t('placement.hintSingle', 'Click on canvas to place')
    }

    // Linear button only for product modes (plants/architecture), not pins
    const linearBtn = isProduct ? `
        <button class="pp-mode-btn${mode === 'linear' ? ' active' : ''}" data-mode="linear">
          <i class="fa-solid fa-grip-lines"></i>
          ${t('placement.linear', 'Linear')}
        </button>` : ''

    return `
      <div class="pp-mode-row">
        <button class="pp-mode-btn${mode === 'single' ? ' active' : ''}" data-mode="single">
          <i class="fa-solid fa-arrow-pointer"></i>
          ${t('placement.single', 'Single')}
        </button>
        <button class="pp-mode-btn${mode === 'continuous' ? ' active' : ''}" data-mode="continuous">
          <i class="fa-solid fa-repeat"></i>
          ${t('placement.continuous', 'Continuous')}
        </button>${linearBtn}
      </div>
      <div class="pp-hint">${hintText}</div>
    `
  }

  private renderDrawToolsBody(layer: 'ground' | 'metadata'): string {
    const activeTool = getViewer()?.getToolManager()?.getActiveToolName() || ''
    const isDrawing = activeTool.startsWith('draw-')
    const prefix = layer === 'ground' ? 'ground' : 'metadata'
    const hintText = isDrawing
      ? t(`${prefix}.hintDraw`, 'Draw on canvas')
      : t(`${prefix}.hintSelectTool`, 'Select a drawing tool to start')

    return `
      <div class="pp-tools-row">
        <button class="pp-tool-btn${activeTool === 'draw-rect' ? ' active' : ''}" data-tool="draw-rect" title="${t(`${prefix}.rectangle`, 'Rectangle')}">
          <i class="fa-regular fa-square"></i>
        </button>
        <button class="pp-tool-btn${activeTool === 'draw-ellipse' ? ' active' : ''}" data-tool="draw-ellipse" title="${t(`${prefix}.ellipse`, 'Ellipse')}">
          <i class="fa-regular fa-circle"></i>
        </button>
        <button class="pp-tool-btn${activeTool === 'draw-polygon' ? ' active' : ''}" data-tool="draw-polygon" title="${t(`${prefix}.polygon`, 'Polygon')}">
          <i class="fa-solid fa-draw-polygon"></i>
        </button>
      </div>
      <div class="pp-tools-row">
        <button class="pp-tool-btn${activeTool === 'draw-path' ? ' active' : ''}" data-tool="draw-path" title="${t('ground.path', 'Path')}">
          <i class="fa-solid fa-route"></i>
        </button>
      </div>
      <div class="pp-hint">${hintText}</div>
    `
  }

  private wireToolButtons(): void {
    this.el.querySelectorAll('.pp-tool-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const toolName = (btn as HTMLElement).dataset.tool
        if (!toolName) return
        const toolManager = getViewer()?.getToolManager()
        if (!toolManager) return
        // Toggle: clicking active tool deactivates it
        if (toolManager.getActiveToolName() === toolName) {
          toolManager.setActiveTool('select')
        } else {
          toolManager.setActiveTool(toolName)
        }
      })
    })
  }

  private wireModeButtons(): void {
    this.el.querySelectorAll('.pp-mode-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const newMode = (btn as HTMLElement).dataset.mode as PlacementMode
        const toolManager = getViewer()?.getToolManager()
        const oldMode = getPlacementMode()

        setPlacementMode(newMode)

        if (toolManager) {
          if (newMode === 'linear') {
            toolManager.setActiveTool('linear-placement')
          } else if (oldMode === 'linear') {
            toolManager.setActiveTool('place-object')
          }
        }
      })
    })
  }

  private updateToolButtons(): void {
    const activeTool = getViewer()?.getToolManager()?.getActiveToolName() || ''
    this.el.querySelectorAll('.pp-tool-btn').forEach(btn => {
      const toolName = (btn as HTMLElement).dataset.tool
      btn.classList.toggle('active', toolName === activeTool)
    })
    // Update hint with correct i18n prefix
    const hintEl = this.el.querySelector('.pp-hint')
    if (hintEl) {
      const mode = getPanelMode()
      const prefix = mode?.type === 'zone' ? 'metadata' : 'ground'
      const isDrawing = activeTool.startsWith('draw-')
      hintEl.textContent = isDrawing
        ? t(`${prefix}.hintDraw`, 'Draw on canvas')
        : t(`${prefix}.hintSelectTool`, 'Select a drawing tool to start')
    }
  }

  private close(): void {
    const mode = getPanelMode()
    if (mode?.type === 'zone') {
      setSelectedZoneType(null)
    } else if (mode?.type === 'pin') {
      setSelectedPinType(null)
    } else {
      stopContinuousPlacement()
    }
    const toolManager = getViewer()?.getToolManager()
    toolManager?.setActiveTool('select')
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  dispose(): void {
    this.unsubscribe?.()
    this.unsubToolChange?.()
    this.el.remove()
  }
}
